import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment-timezone';

import { Box, Typography, Button, TextField, FormControlLabel, FormControl, FormLabel, Radio, RadioGroup } from '@mui/material';
import { Stack, Autocomplete } from '@mui/material';

import DashboardNavbar from '../../Components/DashboardNavbar/DashboardNavbar';
import FileUploadButton from '../../Components/FileManager/FileUploadButton';
import PDFViewButton from '../../Components/FileManager/PDFViewButton';

function Assignment1(props) {
    const { state } = useLocation();
    const [form, setForm] = useState({});
    const [fields, setFields] = useState({
        gpa: "",
        bootcamp: 'no',
        personalStatement: "",
        skills: "",
    });

    const [update, setUpdate] = useState(false);
    const [semester, setSemester] = useState('');

    const [projectRankings, setProjectRankings] = useState();
    const [endSemester, setEndSemester] = useState();

    const [errorMessage, setErrorMessage] = useState("");
    let navigate = useNavigate();

    const handleEndSemesterChange = (event, value) => {
        setErrorMessage("");
        setEndSemester(value);
    };

    const handleChange = (prop) => (event) => {
        setErrorMessage("");
        setFields({ ...fields, [prop]: event.target.value });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        try { 
            const data = {
                form: form,
                gpa: fields.gpa,
                bootcamp: fields.bootcamp === 'yes',
                skills: fields.skills,
                personal_statement: fields.personalStatement,
                end_semester: endSemester,
                is_completed: true,
            };
            await axios.put(`${process.env.REACT_APP_API_HOST}/api/assignment1/update`, data,
            {
                headers: {Authorization: `Bearer ${state.token.access_token}`},
            });
            setUpdate(true);
            setErrorMessage("Assignment has been successfully submitted");
            navigate("/student");
        }
        catch (error) {
            setErrorMessage("Could not submit assignment 1");
        }
    };

    const filesLoaded = (file, type) => {
        setFields((prev) => ({...prev, resume: file}));
    }

    const semesters = ["Fall", "Spring", "Summer", "Fall/Summer"];

    useEffect(() => {
        const getAssignment = async() => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/assignment1/get`, {
                    headers: {Authorization: `Bearer ${state.token.access_token}`},
                });
                if (response.data && response.data.is_completed) {
                    setFields({
                        gpa: response.data.gpa,
                        bootcamp: response.data.bootcamp ? 'yes' : 'no',
                        skills: response.data.skills,
                        personalStatement: response.data.personal_statement,
                        resume: response.data.resume
                    });
                    setEndSemester(response.data.end_semester);
                    setUpdate(true);
                }
                const classData = await axios.get(`${process.env.REACT_APP_API_HOST}/api/class/getClass`, {
                    headers: {Authorization: `Bearer ${state.token.access_token}`},
                });
                setSemester(classData.data.sd1_term);
            }
            catch (error) {
                console.log(error);
            }
        }

        async function getFormData() {
            const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/forms/assignments/${state.assignment.assignment_id}/response`,
                { headers: { Authorization: `Bearer ${state.token.access_token}`} }
            )
            let formData = response.data;
            let start = moment.utc(formData.start, moment.ISO_8601, true);
            let end = moment.utc(formData.end, moment.ISO_8601, true);
            formData.start = start.local().format("dddd, MMMM Do YYYY, h:mm a");
            formData.end = end.local().format("dddd, MMMM Do YYYY, h:mm a");
            setForm(formData);
        }

        const getRankings = async() => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/ranking/getProjectRankingInfo`, 
                    { headers: {Authorization: `Bearer ${state.token.access_token}`}},
                );
                const sortedRankings = response.data.sort((a, b) => a.ranking - b.ranking);
                setProjectRankings(sortedRankings);
            }
            catch (error) {
                console.log(error);
            }
        }

        getFormData();
        getAssignment();
        getRankings();
    }, [state])

    return (
        <Box>
            <DashboardNavbar />
            <Stack direction="row" spacing={4} justifyContent="space-between" sx={{minWidth: 220, p: 2, mx: "7%"}}>
                <Stack spacing={4} component="form" onSubmit={handleSubmit} sx={{width: "50%"}}>
                    <Typography variant="h4">{state.assignment.name}</Typography>
                    <Stack gap={3}>
                        <Autocomplete
                            id="end-semester"
                            options={semesters}
                            value={endSemester || null}
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            autoHighlight
                            autoSelect
                            fullWidth
                            onChange={handleEndSemesterChange}
                            renderInput={(params) => (
                                <TextField {...params} label="Senior Design 2 Semester" variant="outlined" required/>
                            )}
                        />
                        <Box>
                            <label>GPA</label>
                            <input required type="number" step="0.1" min="0.0" max="4.0" value={fields.gpa || ""} onChange={handleChange("gpa")} style={{padding: "10px 10px 3px 3px", border: "none", borderBottom: "black solid 1px", fontSize: "16px"}}/>
                        </Box>
                        <FormControl>
                        <FormLabel id="bootcamp-radio-buttons-label">Will you be attending the Senior Design bootcamp?</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby="bootcamp-radio-buttons-label"
                                name="bootcamp"
                                required
                                value={fields.bootcamp}
                                onChange={handleChange("bootcamp")}
                            >
                                <FormControlLabel value={'yes'} control={<Radio />} label="Yes" />
                                <FormControlLabel value={'no'} control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Stack>
                    <TextField value={fields.personalStatement || ""} style={{maxWidth: "75ch"}} variant="outlined" multiline minRows={7} color="primary" id="personal-statement" label="Personal Statement" onChange={handleChange("personalStatement")}/>
                    <TextField value={fields.skills || ""}style={{maxWidth: "75ch"}} variant="outlined" multiline minRows={4} color="primary" id="skills-interests" label="Skills / Interests" onChange={handleChange("skills")}/>
                    <Stack spacing={1}>
                        <FormLabel id="resume_upload">Upload your resume here</FormLabel>
                        { fields.resume &&
                            <PDFViewButton 
                                file={{ filepath: fields.resume.filepath, filename: fields.resume.filename }}
                            />
                        }
                        <FileUploadButton token={state.token} semester={semester} type='resume' associatedGroupingType="student" host={process.env.REACT_APP_API_HOST} filesLoaded={filesLoaded}/>
                    </Stack>
                    <Typography style={{marginTop: 10, color: "red", alignSelf: "flex-end"}}>{errorMessage}</Typography>
                    <Button variant="contained" type="submit" style={{width: "8rem", alignSelf: "flex-end", marginTop: 10}}>{update ? "Resubmit" : "Submit"}</Button>
                </Stack>
                <Stack style={{width: "40%", marginTop: 10}} spacing={1}>
                    <Typography variant="h5">Current Rankings</Typography>
                    {(projectRankings && projectRankings.length > 0) ? projectRankings.map((item, index) => (
                        <Stack direction="row" spacing={3} key={item.project_id} style={{backgroundColor: "#ECF7FF", padding: 8, borderRadius: 3}}>
                            <Typography>{item.ranking}</Typography>
                            <Typography style={{width: "50%"}}>{item.project.name}</Typography>
                        </Stack>))
                    :
                        <Typography>You have not ranked any projects yet</Typography>
                    }
                </Stack>
            </Stack>
        </Box>
    );
}

export default Assignment1;